import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { CoreActions } from '@next/core-lib';

import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { finalize, map, startWith, switchMap, tap } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';
import { SoilAnalysisApiService } from 'src/app/shared/services/soil-analysis-api.service';

import * as SoilAnalysisActions from './soil-analysis.actions';
import { SoilAnalysisDialogComponent } from '../components/soil-analysis-dialog/soil-analysis-dialog.component';
import { State } from '../models/soil-analysis.models';

@Injectable()
export class SoilAnalysisEffects {
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly api = inject(SoilAnalysisApiService);
  protected readonly dialog = inject(DialogService);
  protected readonly locale = inject(LocaleService);

  openOverview$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SoilAnalysisActions.openOverview),
        tap(() => {
          this.router.navigate(['']);
        }),
      ),
    { dispatch: false },
  );

  getDefaultSoilAnalyses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.loadDefaultSoilAnalyses),
      switchMap((_) =>
        this.api.getDefaultSoilAnalyses().pipe(
          map((defaultSoilAnalyses) => SoilAnalysisActions.loadDefaultSoilAnalysesSuccess({ defaultSoilAnalyses })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  addDefaultSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.addDefaultSoilAnalysis),
      map((_) =>
        SoilAnalysisActions.editDefaultSoilAnalysis({
          defaultSoilAnalysis: {
            id: 0,
            description: '',
            state: State.Draft,
            characteristics: {},
          },
        }),
      ),
    ),
  );

  editDefaultSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.editDefaultSoilAnalysis),
      switchMap((action) =>
        this.dialog
          .form(SoilAnalysisDialogComponent, {
            soilAnalysis: {
              ...action.defaultSoilAnalysis,
            },
            title: action.defaultSoilAnalysis.id ? this.locale.translate('editSoilAnalysis') : this.locale.translate('addSoilAnalysis'),
          })
          .pipe(
            switchMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm ? of(SoilAnalysisActions.upsertDefaultSoilAnalysis({ defaultSoilAnalysis: result })) : EMPTY,
            ),
          ),
      ),
    ),
  );

  upsertDefaultSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.upsertDefaultSoilAnalysis),
      switchMap((action) =>
        this.api.upsertDefaultSoilAnalysis(action.defaultSoilAnalysis).pipe(
          switchMap(() => [
            SoilAnalysisActions.loadDefaultSoilAnalyses(),
            NotificationActions.successSnackbar({
              message: this.locale.translate(action.defaultSoilAnalysis.id === 0 ? 'analysisAdded' : 'soilAnalysisEdited'),
              duration: '5000',
            }),
          ]),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  changeStateDefaultSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.changeStateDefaultSoilAnalysis),
      switchMap((action) =>
        this.dialog
          .confirm({
            title: this.locale.translate('stateChange.title'),
            text: this.locale.translate(`stateChange.${action.state}`),
          })
          .pipe(
            switchMap(({ status }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? this.api.changeStateDefaultSoilAnalysis(action.id, action.state).pipe(
                    switchMap(() => [SoilAnalysisActions.loadDefaultSoilAnalyses()]),
                    finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                    startWith(CoreActions.setLoading({ loading: true })),
                  )
                : EMPTY,
            ),
          ),
      ),
    ),
  );
}
